/**
 * ShareModal Component (Phase 54-03)
 *
 * Modal for sharing on-market property analysis with three actions:
 * - Copy Link: Copies current URL to clipboard
 * - Download PDF: Triggers PDF generation (requires backend endpoint)
 * - Email: Opens mailto: with pre-filled property summary
 *
 * Design: Luxury modal with gold accents matching CRUX RE branding.
 */

import { useState } from 'react';
import { X, Link2, FileDown, Mail, Check, Loader2 } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';

export interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  propertyAddress: string;
  askingPrice?: number;
  suggestedOffer?: number;
  units?: number;
  neighborhood?: string;
  /** Property ID for PDF generation */
  propertyId?: string;
}

export function ShareModal({
  open,
  onClose,
  propertyAddress,
  askingPrice,
  suggestedOffer,
  units,
  neighborhood,
  propertyId,
}: ShareModalProps) {
  const [copied, setCopied] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  if (!open) return null;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleDownloadPDF = async () => {
    if (!propertyId) {
      alert('PDF generation requires a property ID');
      return;
    }

    setPdfLoading(true);
    try {
      // TODO: Implement actual PDF download when backend endpoint is ready
      // const response = await fetch(`/api/generate-pdf/${propertyId}`);
      // const blob = await response.blob();
      // const url = URL.createObjectURL(blob);
      // const a = document.createElement('a');
      // a.href = url;
      // a.download = `${propertyAddress.replace(/\s+/g, '_')}_Analysis.pdf`;
      // a.click();

      // Placeholder: simulate loading
      await new Promise(resolve => setTimeout(resolve, 1000));
      alert('PDF generation coming soon! Backend endpoint needed.');
    } catch (err) {
      console.error('Failed to generate PDF:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleEmail = () => {
    const subject = encodeURIComponent(`Investment Opportunity: ${propertyAddress}`);

    const bodyLines = [
      `I wanted to share this investment opportunity with you:`,
      ``,
      `Property: ${propertyAddress}`,
      neighborhood ? `Location: ${neighborhood}` : '',
      units ? `Units: ${units}` : '',
      askingPrice ? `Asking Price: ${formatCurrency(askingPrice)}` : '',
      suggestedOffer ? `Our Target: ${formatCurrency(suggestedOffer)}` : '',
      ``,
      `View full analysis: ${window.location.href}`,
      ``,
      `---`,
      `Shared via CRUX RE Acquisitions Intelligence`,
    ].filter(Boolean);

    const body = encodeURIComponent(bodyLines.join('\n'));
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  // Modal styles
  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    backdropFilter: 'blur(4px)',
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: '#1c1917',
    border: '1px solid #b45309',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '420px',
    padding: '0',
    position: 'relative',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(251, 191, 36, 0.1)',
  };

  const headerStyle: React.CSSProperties = {
    padding: '20px 24px',
    borderBottom: '1px solid rgba(251, 191, 36, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  const titleStyle: React.CSSProperties = {
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: '14px',
    fontWeight: 600,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: '#fbbf24',
  };

  const closeButtonStyle: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    color: '#a8a29e',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const contentStyle: React.CSSProperties = {
    padding: '24px',
  };

  const propertyInfoStyle: React.CSSProperties = {
    marginBottom: '24px',
    padding: '16px',
    background: 'rgba(251, 191, 36, 0.05)',
    borderRadius: '8px',
    border: '1px solid rgba(251, 191, 36, 0.1)',
  };

  const propertyNameStyle: React.CSSProperties = {
    fontFamily: '"DM Serif Display", serif',
    fontSize: '18px',
    color: '#fef3c7',
    marginBottom: '4px',
  };

  const propertyMetaStyle: React.CSSProperties = {
    fontFamily: '"Inter", sans-serif',
    fontSize: '12px',
    color: '#a8a29e',
  };

  const actionsGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
  };

  const actionButtonStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '20px 12px',
    background: 'rgba(255, 255, 255, 0.02)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  };

  const actionIconStyle: React.CSSProperties = {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const actionLabelStyle: React.CSSProperties = {
    fontFamily: '"Inter", sans-serif',
    fontSize: '12px',
    fontWeight: 500,
    color: '#fef3c7',
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <span style={titleStyle}>Share Analysis</span>
          <button style={closeButtonStyle} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={contentStyle}>
          {/* Property Info */}
          <div style={propertyInfoStyle}>
            <div style={propertyNameStyle}>{propertyAddress}</div>
            <div style={propertyMetaStyle}>
              {[
                neighborhood,
                units && `${units} Units`,
                askingPrice && formatCurrency(askingPrice),
              ].filter(Boolean).join(' • ')}
            </div>
          </div>

          {/* Action Buttons */}
          <div style={actionsGridStyle}>
            {/* Copy Link */}
            <button
              style={{
                ...actionButtonStyle,
                borderColor: copied ? '#34d399' : 'rgba(255, 255, 255, 0.1)',
              }}
              onClick={handleCopyLink}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = '#fbbf24';
                e.currentTarget.style.background = 'rgba(251, 191, 36, 0.05)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = copied ? '#34d399' : 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)';
              }}
            >
              <div style={{ ...actionIconStyle, background: copied ? 'rgba(52, 211, 153, 0.15)' : 'rgba(96, 165, 250, 0.15)' }}>
                {copied ? <Check size={18} color="#34d399" /> : <Link2 size={18} color="#60a5fa" />}
              </div>
              <span style={{ ...actionLabelStyle, color: copied ? '#34d399' : '#fef3c7' }}>
                {copied ? 'Copied!' : 'Copy Link'}
              </span>
            </button>

            {/* Download PDF */}
            <button
              style={actionButtonStyle}
              onClick={handleDownloadPDF}
              disabled={pdfLoading}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = '#fbbf24';
                e.currentTarget.style.background = 'rgba(251, 191, 36, 0.05)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)';
              }}
            >
              <div style={{ ...actionIconStyle, background: 'rgba(251, 191, 36, 0.15)' }}>
                {pdfLoading ? (
                  <Loader2 size={18} color="#fbbf24" className="animate-spin" />
                ) : (
                  <FileDown size={18} color="#fbbf24" />
                )}
              </div>
              <span style={actionLabelStyle}>
                {pdfLoading ? 'Generating...' : 'Download PDF'}
              </span>
            </button>

            {/* Email */}
            <button
              style={actionButtonStyle}
              onClick={handleEmail}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = '#fbbf24';
                e.currentTarget.style.background = 'rgba(251, 191, 36, 0.05)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)';
              }}
            >
              <div style={{ ...actionIconStyle, background: 'rgba(167, 139, 250, 0.15)' }}>
                <Mail size={18} color="#a78bfa" />
              </div>
              <span style={actionLabelStyle}>Email</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ShareModal;
